import React, { useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { Card } from "@/components/ui/card";
import { Table, TableHeader, TableHead, TableRow, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataToolbar, StatusBadge } from "@/components/data/Common";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, ArrowDownCircle, ArrowUpCircle, History } from "lucide-react";

const blankIssue = {
  qty: 0, department: "", equipment_id: "none", work_order_no: "", breakdown_id: "", purpose: "", remarks: "",
  issued_to_type: "user", issued_to_user_id: "", issued_to_name: "", recipient_company: "", recipient_mobile: "",
};
const blankGrn = { qty: 0, unit_cost: 0, vendor: "", invoice_no: "", txn_date: "", remarks: "" };

export default function Inventory() {
  const [list, setList] = useState([]);
  const [projects, setProjects] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ project_id: "", name: "", part_number: "", category: "consumables", uom: "nos", current_stock: 0, min_stock: 0, unit_cost: 0, vendor: "", location: "" });

  const [active, setActive] = useState(null);
  const [issueOpen, setIssueOpen] = useState(false);
  const [grnOpen, setGrnOpen] = useState(false);
  const [histOpen, setHistOpen] = useState(false);
  const [issueForm, setIssueForm] = useState(blankIssue);
  const [grnForm, setGrnForm] = useState(blankGrn);
  const [txns, setTxns] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/projects").then((r) => { setProjects(r.data); if (r.data[0]) setForm((f) => ({ ...f, project_id: r.data[0].id })); });
    api.get("/equipment").then((r) => setEquipment(r.data)).catch(() => {});
    api.get("/users/lookup").then((r) => setUsers(r.data)).catch(() => {});
    load();
  }, []);
  const load = () => api.get("/inventory").then((r) => setList(r.data));

  const submit = async () => {
    try { await api.post("/inventory", form); toast.success("Item added"); setOpen(false); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const openIssue = (i) => { setActive(i); setIssueForm({ ...blankIssue }); setIssueOpen(true); };
  const openGrn = (i) => { setActive(i); setGrnForm({ ...blankGrn, unit_cost: i.unit_cost || 0, vendor: i.vendor || "" }); setGrnOpen(true); };
  const openHist = async (i) => {
    setActive(i); setHistOpen(true); setTxns([]);
    try { const r = await api.get(`/inventory/${i.id}/transactions`); setTxns(r.data); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const submitIssue = async () => {
    if (!issueForm.qty || issueForm.qty <= 0) return toast.error("Enter a valid quantity");
    if (!issueForm.department) return toast.error("Department is required");
    if (!issueForm.purpose) return toast.error("Purpose is required");
    if (issueForm.issued_to_type === "user" && !issueForm.issued_to_user_id) return toast.error("Select a recipient user");
    if (issueForm.issued_to_type === "external" && !issueForm.issued_to_name) return toast.error("Recipient name is required");
    const eq = equipment.find((e) => e.id === issueForm.equipment_id);
    const payload = {
      ...issueForm,
      qty: parseFloat(issueForm.qty),
      equipment_id: issueForm.equipment_id === "none" ? null : issueForm.equipment_id,
      equipment_name: eq ? eq.name : null,
    };
    setSaving(true);
    try {
      await api.post(`/inventory/${active.id}/issue`, payload);
      toast.success("Material issued");
      setIssueOpen(false); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const submitGrn = async () => {
    if (!grnForm.qty || grnForm.qty <= 0) return toast.error("Enter a valid quantity");
    setSaving(true);
    try {
      await api.post(`/inventory/${active.id}/grn`, {
        ...grnForm, qty: parseFloat(grnForm.qty), unit_cost: parseFloat(grnForm.unit_cost) || 0,
        txn_date: grnForm.txn_date || null,
      });
      toast.success("GRN recorded");
      setGrnOpen(false); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const filt = list.filter((i) =>
    (catFilter === "all" || i.category === catFilter) &&
    (!q || `${i.name} ${i.part_number}`.toLowerCase().includes(q.toLowerCase()))
  );
  const low = list.filter((i) => i.current_stock <= i.min_stock).length;

  return (
    <AppLayout title="Spare Parts Inventory" subtitle={`${list.length} items · ${low} below minimum`}>
      <DataToolbar search={q} setSearch={setQ} onAdd={() => setOpen(true)} addLabel="Add Item"
        addTestId="inv-add-button" searchTestId="inv-search-input"
        filters={
          <Select value={catFilter} onValueChange={setCatFilter}>
            <SelectTrigger className="w-40" data-testid="inv-category-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{["all", "critical", "operational", "consumables", "strategic"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        }
      />
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Part #</TableHead><TableHead>Name</TableHead><TableHead>Category</TableHead>
              <TableHead>UoM</TableHead>
              <TableHead className="text-right">Stock</TableHead>
              <TableHead className="text-right">Min</TableHead>
              <TableHead className="text-right">Unit ₹</TableHead>
              <TableHead className="text-right">Value ₹</TableHead>
              <TableHead>Location</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filt.map((i) => {
              const lowItem = i.current_stock <= i.min_stock;
              return (
                <TableRow key={i.id} data-testid={`inv-row-${i.part_number}`}>
                  <TableCell className="font-mono font-semibold">{i.part_number}</TableCell>
                  <TableCell>{i.name}</TableCell>
                  <TableCell><StatusBadge status={i.category} /></TableCell>
                  <TableCell className="text-slate-500 text-xs">{i.uom}</TableCell>
                  <TableCell className="text-right font-mono">
                    {lowItem && <AlertTriangle className="h-3.5 w-3.5 inline mr-1 text-amber-600" />}
                    <span className={lowItem ? "text-red-600 font-semibold" : ""}>{i.current_stock}</span>
                  </TableCell>
                  <TableCell className="text-right font-mono text-slate-500">{i.min_stock}</TableCell>
                  <TableCell className="text-right font-mono">{i.unit_cost?.toLocaleString("en-IN")}</TableCell>
                  <TableCell className="text-right font-mono font-semibold">{(i.current_stock * i.unit_cost).toLocaleString("en-IN")}</TableCell>
                  <TableCell className="text-xs">{i.location}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs mr-1" onClick={() => openIssue(i)} data-testid={`inv-issue-${i.part_number}`}>
                      <ArrowUpCircle className="h-3.5 w-3.5 mr-1 text-red-600" />Issue
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs mr-1" onClick={() => openGrn(i)} data-testid={`inv-grn-${i.part_number}`}>
                      <ArrowDownCircle className="h-3.5 w-3.5 mr-1 text-emerald-600" />GRN
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => openHist(i)} data-testid={`inv-history-${i.part_number}`}>
                      <History className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {!filt.length && <TableRow><TableCell colSpan={10} className="text-center py-12 text-slate-400">No items</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      {/* Add Item Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="inv-create-dialog">
          <DialogHeader><DialogTitle>Add Inventory Item</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="label-cap">Part #</Label><Input data-testid="inv-pn-input" value={form.part_number} onChange={(e) => setForm({ ...form, part_number: e.target.value })} /></div>
            <div><Label className="label-cap">Name</Label><Input data-testid="inv-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div>
              <Label className="label-cap">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="inv-category-input"><SelectValue /></SelectTrigger>
                <SelectContent>{["critical", "operational", "consumables", "strategic"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="label-cap">UoM</Label><Input data-testid="inv-uom-input" value={form.uom} onChange={(e) => setForm({ ...form, uom: e.target.value })} /></div>
            <div><Label className="label-cap">Current Stock</Label><Input type="number" data-testid="inv-stock-input" value={form.current_stock} onChange={(e) => setForm({ ...form, current_stock: parseFloat(e.target.value) || 0 })} /></div>
            <div><Label className="label-cap">Min Stock</Label><Input type="number" data-testid="inv-min-input" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: parseFloat(e.target.value) || 0 })} /></div>
            <div><Label className="label-cap">Unit Cost (₹)</Label><Input type="number" data-testid="inv-cost-input" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: parseFloat(e.target.value) || 0 })} /></div>
            <div><Label className="label-cap">Vendor</Label><Input data-testid="inv-vendor-input" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} /></div>
            <div><Label className="label-cap">Location</Label><Input data-testid="inv-location-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
            <div className="col-span-2">
              <Label className="label-cap">Project</Label>
              <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                <SelectTrigger data-testid="inv-project-input"><SelectValue /></SelectTrigger>
                <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button onClick={submit} className="bg-sky-900 hover:bg-sky-950" data-testid="inv-submit-button">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Issue Dialog */}
      <Dialog open={issueOpen} onOpenChange={setIssueOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="inv-issue-dialog">
          <DialogHeader><DialogTitle>Issue Material — {active?.name} ({active?.part_number})</DialogTitle></DialogHeader>
          <div className="text-xs text-slate-500 mb-1">Available stock: <span className="font-semibold text-slate-700">{active?.current_stock} {active?.uom}</span></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="label-cap">Quantity *</Label><Input type="number" data-testid="issue-qty-input" value={issueForm.qty} onChange={(e) => setIssueForm({ ...issueForm, qty: e.target.value })} /></div>
            <div><Label className="label-cap">Department *</Label><Input data-testid="issue-dept-input" value={issueForm.department} onChange={(e) => setIssueForm({ ...issueForm, department: e.target.value })} /></div>
            <div>
              <Label className="label-cap">Equipment</Label>
              <Select value={issueForm.equipment_id} onValueChange={(v) => setIssueForm({ ...issueForm, equipment_id: v })}>
                <SelectTrigger data-testid="issue-equipment-input"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {equipment.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="label-cap">Work Order No.</Label><Input data-testid="issue-wo-input" value={issueForm.work_order_no} onChange={(e) => setIssueForm({ ...issueForm, work_order_no: e.target.value })} /></div>
            <div><Label className="label-cap">Breakdown ID</Label><Input data-testid="issue-breakdown-input" value={issueForm.breakdown_id} onChange={(e) => setIssueForm({ ...issueForm, breakdown_id: e.target.value })} /></div>
            <div>
              <Label className="label-cap">Issued To *</Label>
              <Select value={issueForm.issued_to_type} onValueChange={(v) => setIssueForm({ ...issueForm, issued_to_type: v, issued_to_user_id: "", issued_to_name: "" })}>
                <SelectTrigger data-testid="issue-to-type-input"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">System User</SelectItem>
                  <SelectItem value="external">External Person / Contractor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {issueForm.issued_to_type === "user" ? (
              <div className="col-span-2">
                <Label className="label-cap">Recipient (User) *</Label>
                <Select value={issueForm.issued_to_user_id} onValueChange={(v) => setIssueForm({ ...issueForm, issued_to_user_id: v })}>
                  <SelectTrigger data-testid="issue-to-user-input"><SelectValue placeholder="Select user" /></SelectTrigger>
                  <SelectContent>
                    {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <>
                <div><Label className="label-cap">Recipient Name *</Label><Input data-testid="issue-ext-name-input" value={issueForm.issued_to_name} onChange={(e) => setIssueForm({ ...issueForm, issued_to_name: e.target.value })} /></div>
                <div><Label className="label-cap">Company / Contractor</Label><Input data-testid="issue-ext-company-input" value={issueForm.recipient_company} onChange={(e) => setIssueForm({ ...issueForm, recipient_company: e.target.value })} /></div>
                <div><Label className="label-cap">Mobile (optional)</Label><Input data-testid="issue-ext-mobile-input" value={issueForm.recipient_mobile} onChange={(e) => setIssueForm({ ...issueForm, recipient_mobile: e.target.value })} /></div>
              </>
            )}
            <div className="col-span-2"><Label className="label-cap">Purpose of Issue *</Label><Input data-testid="issue-purpose-input" value={issueForm.purpose} onChange={(e) => setIssueForm({ ...issueForm, purpose: e.target.value })} /></div>
            <div className="col-span-2"><Label className="label-cap">Remarks</Label><Textarea data-testid="issue-remarks-input" value={issueForm.remarks} onChange={(e) => setIssueForm({ ...issueForm, remarks: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button onClick={submitIssue} disabled={saving} className="bg-sky-900 hover:bg-sky-950" data-testid="issue-submit-button">{saving ? "Saving…" : "Confirm Issue"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* GRN Dialog */}
      <Dialog open={grnOpen} onOpenChange={setGrnOpen}>
        <DialogContent className="max-w-xl" data-testid="inv-grn-dialog">
          <DialogHeader><DialogTitle>Goods Receipt (GRN) — {active?.name} ({active?.part_number})</DialogTitle></DialogHeader>
          <div className="text-xs text-slate-500 mb-1">Current stock: <span className="font-semibold text-slate-700">{active?.current_stock} {active?.uom}</span> · Unit ₹{active?.unit_cost?.toLocaleString("en-IN")}</div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="label-cap">Quantity Received *</Label><Input type="number" data-testid="grn-qty-input" value={grnForm.qty} onChange={(e) => setGrnForm({ ...grnForm, qty: e.target.value })} /></div>
            <div><Label className="label-cap">Unit Cost (₹)</Label><Input type="number" data-testid="grn-cost-input" value={grnForm.unit_cost} onChange={(e) => setGrnForm({ ...grnForm, unit_cost: e.target.value })} /></div>
            <div><Label className="label-cap">Vendor</Label><Input data-testid="grn-vendor-input" value={grnForm.vendor} onChange={(e) => setGrnForm({ ...grnForm, vendor: e.target.value })} /></div>
            <div><Label className="label-cap">Invoice No.</Label><Input data-testid="grn-invoice-input" value={grnForm.invoice_no} onChange={(e) => setGrnForm({ ...grnForm, invoice_no: e.target.value })} /></div>
            <div><Label className="label-cap">Date</Label><Input type="date" data-testid="grn-date-input" value={grnForm.txn_date} onChange={(e) => setGrnForm({ ...grnForm, txn_date: e.target.value })} /></div>
            <div className="col-span-2"><Label className="label-cap">Remarks</Label><Textarea data-testid="grn-remarks-input" value={grnForm.remarks} onChange={(e) => setGrnForm({ ...grnForm, remarks: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button onClick={submitGrn} disabled={saving} className="bg-sky-900 hover:bg-sky-950" data-testid="grn-submit-button">{saving ? "Saving…" : "Record GRN"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={histOpen} onOpenChange={setHistOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="inv-history-dialog">
          <DialogHeader><DialogTitle>Transaction History — {active?.name} ({active?.part_number})</DialogTitle></DialogHeader>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead><TableHead>Type</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Before</TableHead>
                  <TableHead className="text-right">After</TableHead>
                  <TableHead>Party</TableHead><TableHead>Ref / Purpose</TableHead><TableHead>By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {txns.map((t) => (
                  <TableRow key={t.id} data-testid={`txn-row-${t.id}`}>
                    <TableCell className="text-xs whitespace-nowrap">{(t.txn_date || t.created_at || "").slice(0, 10)}</TableCell>
                    <TableCell>
                      <span className={`text-xs px-2 py-0.5 rounded font-semibold ${t.txn_type === "grn" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                        {t.txn_type === "grn" ? "GRN" : "ISSUE"}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-mono">{t.qty}</TableCell>
                    <TableCell className="text-right font-mono text-slate-500">{t.stock_before}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">{t.stock_after}</TableCell>
                    <TableCell className="text-xs">{t.txn_type === "grn" ? (t.vendor || "—") : (t.issued_to_name || "—")}{t.recipient_company ? ` (${t.recipient_company})` : ""}</TableCell>
                    <TableCell className="text-xs">{t.txn_type === "grn" ? (t.invoice_no || "—") : (t.purpose || "—")}</TableCell>
                    <TableCell className="text-xs text-slate-500">{t.issued_by_name || t.received_by_name || "—"}</TableCell>
                  </TableRow>
                ))}
                {!txns.length && <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-400">No transactions yet</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
